'use client';

import { useCurrentTime } from '@shared/hooks';
import { useTimeZone } from 'next-intl';
import type { LotteryName } from '../../../lottery-purchases/types';
import { useLotteryCategory } from '../../hooks/useLotteryCategory/useLotteryCategory';
import { type LotteryCategoryLayoutContainerProps, LotteryCategoryLayout } from '../../LotteryCategoryLayout/LotteryCategoryLayout';
import { filterTodaysDraws } from '../utils/filters';

const CATEGORY = 'todays_draws';

type LotteryCategoryTodaysDrawsContentProps = LotteryCategoryLayoutContainerProps & {
  lotteries: LotteryName[];
  skeletonItems?: number;
  emptyMessage?: React.ReactNode;
};

/**
 * Client boundary for the todays-draws lottery category page. It's kept separate from
 * LotteryCategoryPageContent because its filter depends on the current time — isolating
 * the ticking clock hooks (useTimeZone/useCurrentTime) here means the other category pages
 * never subscribe to them.
 */
function LotteryCategoryTodaysDrawsContent({ lotteries, skeletonItems, emptyMessage, ...props }: LotteryCategoryTodaysDrawsContentProps) {
  const { data, isLoading } = useLotteryCategory(CATEGORY, lotteries);
  const timeZone = useTimeZone();
  const currentTime = useCurrentTime({ resolution: 60_000, updatePeriod: 5_000 });
  const lotteriesData = filterTodaysDraws(data, timeZone, new Date(currentTime));

  return <LotteryCategoryLayout category={CATEGORY} isLoading={isLoading} lotteriesData={lotteriesData} skeletonItems={skeletonItems} emptyMessage={emptyMessage} {...props} />;
}

export { LotteryCategoryTodaysDrawsContent };
